import React from "react";
import getConfig from "next/config";
import {
  AsyncMethodReturns,
  Connection,
  connectToChild,
  connectToParent,
  ErrorCode,
} from "penpal";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import {
  WidgetConfigValue,
  WidgetData,
  WidgetProps,
  WidgetVariableValue,
} from "../../lib/types";
import { captureException, captureMessage } from "@sentry/nextjs";

const {
  publicRuntimeConfig: { widgetRendererUri },
} = getConfig();

const WidgetContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

type RendererInterface = {
  render: (
    templateId: string,
    versionNumber: number,
    buildNumber: number,
    props: WidgetProps,
  ) => void;
  updateConfig: (config: Record<string, WidgetConfigValue>) => void;
  updateParameters: (parameters: Record<string, WidgetVariableValue>) => void;
  updateData: (data: Record<string, unknown>) => void;
  setVisible: (visible: boolean) => void;
  enablePreview: () => void;
  willDisappear: () => void;
};

export type Props = WidgetData & {
  preview?: boolean;
  visible?: boolean;
  willDisappear?: boolean;
  onReady?: () => void;
  onEnded?: () => void;
  onReloadData?: (config: Record<string, WidgetConfigValue>) => void;
};

export default function Widget({
  templateId,
  versionNumber,
  buildNumber,
  type,
  data,
  visible,
  config: initialConfig,
  parameters,
  preview,
  willDisappear,
  onReady,
  onEnded,
  onReloadData,
}: Props) {
  const router = useRouter();

  const debug = Boolean(router.query.debug);

  const frameRef = React.createRef<HTMLIFrameElement>();

  const [ready, setReady] = React.useState<boolean>(false);
  const [child, setChild] =
    React.useState<AsyncMethodReturns<RendererInterface>>();
  const [config, setConfig] =
    React.useState<Record<string, WidgetConfigValue>>(initialConfig);

  React.useEffect(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  React.useEffect(() => {
    const connection = connectToParent({
      methods: {
        setConfig: (config: Record<string, WidgetConfigValue>) => {
          setConfig(config);
        },
        setParameters: (parameters: Record<string, WidgetVariableValue>) => {
          child?.updateParameters(parameters);
        },
        setData: (data: Record<string, unknown>) => {
          child?.updateData(data);
        },
      },
    });

    connection.promise.then((parent) => {});

    return () => connection.destroy();
  }, [child]);

  React.useEffect(() => {
    if (!frameRef.current) return;

    let connection: Connection<RendererInterface>;

    const connect = (iframe: HTMLIFrameElement) => {
      connection = connectToChild<RendererInterface>({
        iframe,
        methods: {
          ready() {
            setReady(true);
            if (onReady) onReady();
          },
          ended() {
            if (onEnded) onEnded();
          },
        },
        timeout: 5_000,
        debug,
      });

      connection.promise
        .then((child) => {
          setChild(child);
        })
        .catch((e) => {
          if (e.code !== ErrorCode.ConnectionTimeout) {
            captureException(e);
          } else {
            connect(iframe);
          }
        });

      return connection;
    };

    connect(frameRef.current);

    frameRef.current.onload = () => {
      console.log("Widget iframe loaded");
    };

    frameRef.current.onerror = (e: string | Event) => {
      console.error(e);

      captureMessage(`Widget ${templateId} failed to load`, {
        extra: {
          error: e,
        },
      });
    };

    return () => {
      setReady(false);
      setChild(undefined);

      if (connection) {
        connection.destroy();
      }
    };
  }, [frameRef.current]);

  React.useEffect(() => {
    if (!child || !ready) return;

    child.render(templateId, versionNumber, buildNumber, {
      data,
      config,
      parameters,
    });
  }, [child, ready]);

  React.useEffect(() => {
    if (!child || !ready) return;
    child?.updateData(data);
  }, [child, data, ready]);

  React.useEffect(() => {
    if (!child || !ready) return;
    child?.updateParameters(parameters);
  }, [child, parameters, ready]);

  React.useEffect(() => {
    if (!child || !ready) return;
    child?.updateConfig(config);
  }, [child, config, ready]);

  React.useEffect(() => {
    if (!child || !preview || !ready) return;
    child.enablePreview();
  }, [child, preview, ready]);

  React.useEffect(() => {
    if (!child || !visible || !ready) return;

    if (typeof child.setVisible !== "undefined") {
      child.setVisible(visible);
    }
  }, [child, visible, ready]);

  React.useEffect(() => {
    if (!child || !willDisappear || !ready) return;
    child.willDisappear();
  }, [child, willDisappear, ready]);

  React.useEffect(() => {
    if (onReloadData && type === "qrcode") {
      onReloadData(config);
    }
  }, [type, config]);

  return (
    <WidgetContainer key={`embed-container`}>
      <iframe
        key={`embed-iframe`}
        ref={frameRef}
        src={`${widgetRendererUri}${debug ? "?debug=1" : ""}`}
      />
    </WidgetContainer>
  );
}
